// Creative v2 global context (docs/CREATIVE_V2.md): the per-tenant flag and the one date
// window every creative page reads. Pages never own the window; the header does.
import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { useAuth } from './auth'
import '../kit/kit.css'

/** On when the tenant is listed in CREATIVE_V2_TENANTS (server), or for super admins previewing. */
export function useCreativeV2(): boolean {
  const { me } = useAuth()
  return !!(me?.features?.creative_v2 || me?.is_super_admin)
}

export type Preset = '7d' | '28d' | '90d' | 'custom'
export interface DateWindow { preset: Preset; start: string; end: string; compare: boolean }
export interface WindowBounds { start: string; end: string; days: number; prior_start?: string; prior_end?: string }

const KEY = 'cv2Window'
const DEFAULT: DateWindow = { preset: '28d', start: '', end: '', compare: true }
function load(): DateWindow {
  try { const raw = sessionStorage.getItem(KEY); if (raw) return { ...DEFAULT, ...JSON.parse(raw) } } catch { /* storage blocked */ }
  return DEFAULT
}

/** Query-string fragment for the v2 endpoints: window=28d or start&end, plus compare. */
export function windowQuery(w: DateWindow): string {
  const base = w.preset === 'custom' && w.start && w.end ? `start=${w.start}&end=${w.end}` : `window=${w.preset === 'custom' ? '28d' : w.preset}`
  return `${base}${w.compare ? '&compare=1' : ''}`
}

/** "Sep 28 to Oct 25 vs Aug 31 to Sep 27" from the bounds the server actually used. */
export function describeWindow(b: WindowBounds | null | undefined, compare: boolean): string {
  if (!b) return ''
  const f = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  return `${f(b.start)} to ${f(b.end)} (${b.days}d)${compare && b.prior_start && b.prior_end ? ` vs ${f(b.prior_start)} to ${f(b.prior_end)}` : ''}`
}

const Ctx = createContext<{ win: DateWindow; setWin: (w: DateWindow) => void; q: string } | null>(null)

export function DateWindowProvider({ children }: { children: ReactNode }) {
  const [win, setWinState] = useState<DateWindow>(load)
  const setWin = (w: DateWindow) => { setWinState(w); try { sessionStorage.setItem(KEY, JSON.stringify(w)) } catch { /* storage blocked */ } }
  const value = useMemo(() => ({ win, setWin, q: windowQuery(win) }), [win])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useDateWindow() {
  const c = useContext(Ctx)
  if (!c) throw new Error('useDateWindow outside DateWindowProvider')
  return c
}

/** The header picker: 7d / 28d / 90d / custom dates, and compare to the prior period of equal length. */
export function DateWindowPicker() {
  const { win, setWin } = useDateWindow()
  const yesterday = new Date(Date.now() - 864e5).toISOString().slice(0, 10)
  return (
    <div className="kit-window" aria-label="Date window">
      <div className="seg">
        {(['7d', '28d', '90d', 'custom'] as Preset[]).map(p => (
          <button key={p} aria-pressed={win.preset === p} onClick={() => setWin({ ...win, preset: p,
            start: p === 'custom' && !win.start ? new Date(Date.now() - 28 * 864e5).toISOString().slice(0, 10) : win.start,
            end: p === 'custom' && !win.end ? yesterday : win.end })}>{p === 'custom' ? 'Custom' : p.toUpperCase()}</button>
        ))}
      </div>
      {win.preset === 'custom' && <>
        <input type="date" value={win.start} max={win.end || yesterday} onChange={e => setWin({ ...win, start: e.target.value })} aria-label="Start date" />
        <input type="date" value={win.end} min={win.start} max={yesterday} onChange={e => setWin({ ...win, end: e.target.value })} aria-label="End date" />
      </>}
      <label><input type="checkbox" checked={win.compare} onChange={e => setWin({ ...win, compare: e.target.checked })} />vs prior</label>
    </div>
  )
}
